import React, { Component } from 'react';
import classNames from 'classnames';
import { withContext } from '../utils/StateProvider';
import { tileClass } from './mapScreen';
import { TILE } from '../utils/dungeon';
import { PAINTABLE_FLOORS } from '../utils/textures';
import {
  blankMap, randomMap, paint, paintBlocker, resize, mapBlocker, describeMap, isBuilding,
  listMaps, saveMap, deleteMap, findMap, toJson, fromJson,
  ENTRANCES, MIN_SIZE, MAX_SIZE, DEFAULT_SIZE, MAX_NAME,
} from '../utils/maps';

// What can be painted, in the order the palette shows it
const BRUSHES = [
  {
    group: 'Ground and walls',
    tools: [
      { label: 'Floor', tile: TILE.floor },
      { label: 'Wall', tile: 1 },
    ],
  },
  {
    group: 'Doors',
    tools: [
      { label: 'Door —', tile: 3 },
      { label: 'Open —', tile: 4 },
      { label: 'Door |', tile: 5 },
      { label: 'Open |', tile: 6 },
      { label: 'Broken', tile: TILE.brokenDoor },
    ],
  },
  {
    // What the party walks up to and uses, inside the shop
    group: 'Counters',
    tools: [
      { label: 'Altar', tile: TILE.temple },
      { label: 'Lectern', tile: TILE.magicSchool },
      { label: 'Shop counter', tile: TILE.market },
      { label: "Trainer's desk", tile: TILE.trainer },
      { label: 'Bar', tile: TILE.tavern },
    ],
  },
  {
    // ...and the front painted on the building outside, beside its door
    group: 'Shop fronts',
    tools: [
      { label: 'Temple front', surround: 'temple' },
      { label: 'School front', surround: 'magic' },
      { label: 'Market front', surround: 'market' },
      { label: 'Training front', surround: 'training' },
      { label: 'Tavern front', surround: 'tavern' },
      { label: 'No front', surround: null },
    ],
  },
  {
    group: 'Dungeon furniture',
    tools: [
      { label: 'Chest', tile: TILE.chest },
      { label: 'Stairs down', tile: TILE.stairsDown },
    ],
  },
  {
    group: 'Markers',
    tools: [
      { label: 'Party starts here', marker: 'start' },
      { label: 'Way down', marker: 'entrance' },
    ],
  },
];

const FLOOR_BRUSHES = [
  { label: 'Clear', floor: null },
  ...PAINTABLE_FLOORS.map((floor) => ({ label: floor.name, floor: floor.id })),
];

const sameTool = (a, b) =>
  a && b && a.tile === b.tile && a.marker === b.marker && a.floor === b.floor &&
  a.surround === b.surround;

// Paint a map square by square: the editor for towns, and whatever else
// comes later. Maps are kept in the browser beside the saved parties.
class MapEditor extends Component {
  state = {
    map: blankMap(DEFAULT_SIZE, ''),
    tool: BRUSHES[0].tools[0],
    painting: false,
    saved: null,
    message: null,
    sharing: false,
    share: '',
  };

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('mouseup', this.stopPainting);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('mouseup', this.stopPainting);
  }

  handleKeydown = (e) => {
    if (e.key === 'Escape') this.props.context.closeEditor();
  }

  stopPainting = () => this.setState({ painting: false });

  // ---------------------------------------------------------------- painting

  apply = (x, y) => {
    this.setState((prev) => {
      const refused = paintBlocker(prev.map, [x, y], prev.tool);
      if (refused) return { message: refused };
      return { map: paint(prev.map, [x, y], prev.tool), saved: null, message: null };
    });
  }

  startPainting = (x, y) => {
    this.setState({ painting: true });
    this.apply(x, y);
  }

  paintOver = (x, y) => {
    if (this.state.painting) this.apply(x, y);
  }

  // ---------------------------------------------------------------- the file

  rename = (name) => this.setState((prev) => ({ map: { ...prev.map, name }, saved: null }));

  setSize = (size) => this.setState((prev) => ({ map: resize(prev.map, size), saved: null }));

  setEntrance = (style) =>
    this.setState((prev) => ({ map: { ...prev.map, entranceStyle: style }, saved: null }));

  fresh = (how) => {
    const { map } = this.state;
    const build = how === 'random' ? randomMap : blankMap;
    this.setState({ map: build(map.size, map.name), saved: null, message: null });
  }

  save = () => {
    const saved = saveMap(this.state.map);
    this.setState(saved
      ? { saved: saved.name, message: `Saved as "${saved.name}".` }
      : { message: 'The map needs a name before it can be saved.' });
  }

  load = (name) => {
    const found = findMap(name);
    if (found) this.setState({ map: { ...blankMap(found.size), ...found }, saved: name, message: null });
  }

  remove = (name) => {
    deleteMap(name);
    this.setState({ saved: null, message: `Deleted "${name}".` });
  }

  // Maps travel as plain JSON, so one can be passed to somebody else
  share = () => this.setState((prev) => ({ sharing: !prev.sharing, share: toJson(prev.map), message: null }));

  accept = () => {
    try {
      this.setState({ map: fromJson(this.state.share), sharing: false, saved: null, message: 'Map loaded.' });
    } catch (err) {
      this.setState({ message: `That is not a map: ${err.message}` });
    }
  }

  play = () => {
    const blocker = mapBlocker(this.state.map);
    if (blocker) {
      this.setState({ message: blocker });
      return;
    }
    const { startRun, newSeed } = this.props.context;
    startRun(newSeed(), 10, null, this.state.map);
  }

  // ---------------------------------------------------------------- drawing

  renderPalette() {
    const { tool } = this.state;
    const button = (brush, index) => (
      <button
        key={brush.label + index}
        className={classNames('editor-brush', { chosen: sameTool(tool, brush) })}
        onClick={() => this.setState({ tool: brush })}
      >
        {brush.label}
      </button>
    );

    return (
      <div className="editor-palette">
        {BRUSHES.map((section) => (
          <div key={section.group} className="editor-section">
            <h2>{section.group}</h2>
            <div className="editor-brushes">{section.tools.map(button)}</div>
          </div>
        ))}
        <div className="editor-section">
          <h2>Ground cover</h2>
          <div className="editor-brushes">{FLOOR_BRUSHES.map(button)}</div>
        </div>
      </div>
    );
  }

  renderGrid() {
    const { map } = this.state;
    const cell = Math.max(10, Math.min(28, Math.floor(620 / map.size)));
    const layout = {
      gridTemplateColumns: `repeat(${map.size}, ${cell}px)`,
      gridAutoRows: `${cell}px`,
      fontSize: `${Math.round(cell * 0.62)}px`,
      lineHeight: `${cell}px`,
    };
    const marker = (x, y) => {
      if (map.start && map.start[0] === x && map.start[1] === y) return 'start';
      if (map.entrance && map.entrance[0] === x && map.entrance[1] === y) return 'entrance';
      return null;
    };

    return (
      <div className="editor-grid" style={layout}>
        {map.tiles.map((row, y) => row.map((tile, x) => {
          const floor = map.floors[y][x];
          const front = map.surrounds && map.surrounds[y][x];
          const here = marker(x, y);
          return (
            <div
              key={`${x}:${y}`}
              className={classNames('map-cell', tileClass(tile),
                                    floor && !isBuilding(tile) && `floor-${floor}`,
                                    front && 'editor-front', here && `editor-${here}`)}
              title={front ? `${front} front` : undefined}
              onMouseDown={() => this.startPainting(x, y)}
              onMouseEnter={() => this.paintOver(x, y)}
            >
              {here === 'start' ? '@' : here === 'entrance' ? '▼' : tile === TILE.stairsDown ? '▼' : ''}
            </div>
          );
        }))}
      </div>
    );
  }

  render() {
    const { map, saved, message, sharing } = this.state;
    const { closeEditor } = this.props.context;
    const blocker = mapBlocker(map);
    const maps = listMaps();

    return (
      <div id="mapEditor">
        <div className="editor-panel">
          <div className="sheet-header">
            <div>
              <h1>Map Editor</h1>
              <div className="sheet-subtitle">{describeMap(map)}</div>
            </div>
            <div className="sheet-close" onClick={closeEditor}>LEAVE (ESC)</div>
          </div>

          <div className="editor-body">
            {this.renderPalette()}

            <div className="editor-middle">
              {this.renderGrid()}
              <div className={classNames('editor-status', { blocked: !!blocker })}>
                {message || blocker || 'Ready to play.'}
              </div>
            </div>

            <div className="editor-side">
              <div className="editor-section">
                <h2>This map</h2>
                <input
                  value={map.name}
                  maxLength={MAX_NAME}
                  placeholder="Map name"
                  onChange={(e) => this.rename(e.target.value)}
                />
                <div className="editor-row">
                  <span>Size</span>
                  <button className="menu-button small" onClick={() => this.setSize(map.size - 1)}>−</button>
                  <span className="editor-size">{map.size}</span>
                  <button className="menu-button small" onClick={() => this.setSize(map.size + 1)}>+</button>
                </div>
                <div className="sheet-muted">{MIN_SIZE} to {MAX_SIZE} squares across</div>

                <div className="editor-row">
                  <button className="menu-button small" onClick={() => this.fresh('blank')}>BLANK</button>
                  <button className="menu-button small" onClick={() => this.fresh('random')}>RANDOM TOWN</button>
                </div>
              </div>

              <div className="editor-section">
                <h2>Way down</h2>
                <div className="editor-brushes">
                  {Object.entries(ENTRANCES).map(([style, entrance]) => (
                    <button
                      key={style}
                      className={classNames('editor-brush', { chosen: map.entranceStyle === style })}
                      onClick={() => this.setEntrance(style)}
                    >
                      {entrance.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="editor-section">
                <h2>Keeping it</h2>
                <div className="editor-row">
                  <button className="menu-button small" onClick={this.save}>
                    {saved === map.name ? 'SAVED' : 'SAVE'}
                  </button>
                  <button className="menu-button small" onClick={this.share}>
                    {sharing ? 'CLOSE' : 'EXPORT'}
                  </button>
                  <button
                    className={classNames('menu-button', 'small', { disabled: !!blocker })}
                    onClick={this.play}
                  >
                    PLAY IN IT
                  </button>
                </div>

                {sharing && (
                  <div className="editor-share">
                    <textarea
                      value={this.state.share}
                      spellCheck={false}
                      onChange={(e) => this.setState({ share: e.target.value })}
                    />
                    <button className="menu-button small" onClick={this.accept}>LOAD THIS</button>
                  </div>
                )}

                {maps.map((entry) => (
                  <div key={entry.name} className="editor-row editor-saved">
                    <button className="menu-button small" onClick={() => this.load(entry.name)}>
                      {entry.name}
                    </button>
                    <span className="sheet-muted">{entry.size}×{entry.size}</span>
                    <span className="editor-delete" onClick={() => this.remove(entry.name)}>✕</span>
                  </div>
                ))}
                {!maps.length && <div className="sheet-muted">No maps saved yet.</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withContext(MapEditor);
